import { getLocalDateKey, normalizeToLocalDate } from "utils/dateTime";
import { parseTimeToMinutes } from "utils/scheduleTimelineUtils";
import { sortSchedulesByOrder } from "utils/scheduleEditorUtils";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const LEGACY_TEMPLATE_PREFIX = "legacy:";
export const NO_SCHOOL_TEMPLATE_ID = "__NO_SCHOOL__";
export const NO_SCHEDULE_TEMPLATE_ID = "__NO_SCHEDULE__";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const normalizeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeDateInput = (input) => normalizeToLocalDate(input);

export const formatDateKey = (input) => getLocalDateKey(input);

export const getOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) return "th";
  const lastDigit = day % 10;
  if (lastDigit === 1) return "st";
  if (lastDigit === 2) return "nd";
  if (lastDigit === 3) return "rd";
  return "th";
};

/** e.g. "Friday - May 29th, 2026" */
const sortTimelineClasses = (firstClass, secondClass) => {
  const firstMinutes = parseTimeToMinutes(firstClass.startTime) ?? 0;
  const secondMinutes = parseTimeToMinutes(secondClass.startTime) ?? 0;
  if (firstMinutes !== secondMinutes) return firstMinutes - secondMinutes;
  return String(firstClass.id || "").localeCompare(String(secondClass.id || ""));
};

export const buildCalendarOnlyScheduleTimelineClasses = ({ classes = [], resolvedSchedule = {} }) => {
  const templateClasses = Array.isArray(resolvedSchedule?.template?.classes)
    ? resolvedSchedule.template.classes
    : [];

  return templateClasses
    .filter((scheduleClass) => scheduleClass?.startTime && scheduleClass?.endTime)
    .map((scheduleClass, index) => {
      const classItem = (Array.isArray(classes) ? classes : []).find(
        (candidate) => String(candidate?.classId) === String(scheduleClass?.classId)
      );
      return {
        id: String(scheduleClass.id || `${scheduleClass.classId || "class"}-${scheduleClass.startTime}-${index}`),
        classId: scheduleClass.classId ? String(scheduleClass.classId) : null,
        name: String(scheduleClass.name || scheduleClass.className || classItem?.className || "Class"),
        startTime: String(scheduleClass.startTime),
        endTime: String(scheduleClass.endTime),
      };
    })
    .sort(sortTimelineClasses);
};

export const formatLongDisplayDate = (input) => {
  const date = normalizeDateInput(input);
  if (Number.isNaN(date.getTime())) return "";

  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const monthName = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();
  const year = date.getFullYear();

  return `${weekday} - ${monthName} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

export const getDayNameForDate = (input) => {
  const date = normalizeDateInput(input);
  return DAY_NAMES[date.getDay()];
};

const getStartOfIsoWeek = (input) => {
  const date = normalizeDateInput(input);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
};

const countWeekdayOffset = (fromInput, toInput) => {
  const from = normalizeDateInput(fromInput);
  const to = normalizeDateInput(toInput);
  if (from.getTime() === to.getTime()) return 0;

  const step = from < to ? 1 : -1;
  const pointer = new Date(from);
  let offset = 0;

  while (pointer.getTime() !== to.getTime()) {
    pointer.setDate(pointer.getDate() + step);
    const day = pointer.getDay();
    if (day !== 0 && day !== 6) {
      offset += step;
    }
  }

  return offset;
};

const countIsoWeekOffset = (fromInput, toInput) => {
  const from = getStartOfIsoWeek(fromInput);
  const to = getStartOfIsoWeek(toInput);
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  return Math.round((to.getTime() - from.getTime()) / msPerWeek);
};

const mod = (value, divisor) => ((value % divisor) + divisor) % divisor;

const getDebugOrTodayDate = () => {
  if (typeof window !== "undefined" && window.localStorage) {
    const debugDate = window.localStorage.getItem("debugCycleDate");
    if (debugDate) {
      return normalizeDateInput(debugDate);
    }
  }
  return normalizeDateInput(new Date());
};

const getScheduleCalendarConfig = (settings = {}) => {
  const scheduleCalendar = isPlainObject(settings?.scheduleCalendar) ? settings.scheduleCalendar : {};
  return {
    templates: Array.isArray(scheduleCalendar.templates) ? scheduleCalendar.templates : [],
    assignments: Array.isArray(scheduleCalendar.assignments) ? scheduleCalendar.assignments : [],
  };
};

const createLegacyTemplate = (settings, scheduleKey) => {
  const isDailyCycle = settings?.multiWeek && settings?.cycleType === "day";
  const isWeeklyCycle = settings?.multiWeek && settings?.cycleType === "week";

  if (!settings?.multiWeek) {
    return {
      id: `${LEGACY_TEMPLATE_PREFIX}A`,
      label: "Schedule A",
      scheduleKey: "A",
      source: "legacy",
      dayMode: "actualWeekday",
      active: true,
      isLegacy: true,
    };
  }

  return {
    id: `${LEGACY_TEMPLATE_PREFIX}${scheduleKey}`,
    label: isDailyCycle ? `Day ${scheduleKey}` : isWeeklyCycle ? `Week ${scheduleKey}` : scheduleKey,
    scheduleKey,
    source: "legacy",
    dayMode: isDailyCycle ? "mondayOnly" : "actualWeekday",
    active: true,
    isLegacy: true,
  };
};

export const getLegacyScheduleTemplates = (settings = {}) => {
  if (!settings?.multiWeek) {
    return [createLegacyTemplate(settings, "A")];
  }

  const total = Math.max(1, normalizeNumber(settings?.numberOfWeeks, 1));
  return Array.from({ length: total }, (_, index) =>
    createLegacyTemplate(settings, String.fromCharCode(65 + index))
  );
};

export const getCustomScheduleTemplates = (settings = {}) => {
  const { templates } = getScheduleCalendarConfig(settings);
  return sortSchedulesByOrder(templates)
    .filter((template) => template?.id && template?.label && template?.active !== false)
    .map((template) => ({
      ...template,
      id: String(template.id),
      label: String(template.label),
      source: "custom",
      scheduleKey: String(template.scheduleKey || template.id),
      dayMode: template.dayMode === "actualWeekday" ? "actualWeekday" : "mondayOnly",
      isLegacy: false,
    }));
};

export const getAllScheduleTemplates = (settings = {}) => [
  ...getLegacyScheduleTemplates(settings),
  ...getCustomScheduleTemplates(settings),
];

export const getTemplateById = (settings = {}, templateId) =>
  getAllScheduleTemplates(settings).find((template) => template.id === templateId) || null;

export const resolveLegacyTemplateForDate = (settings = {}, activeDate) => {
  const date = normalizeDateInput(activeDate);
  const actualDayName = getDayNameForDate(date);

  if (!settings?.multiWeek) {
    const template = createLegacyTemplate(settings, "A");
    return {
      templateId: template.id,
      template,
      scheduleKey: "A",
      dayName: actualDayName,
      source: "legacy",
      date,
      dateKey: formatDateKey(date),
      label: template.label,
    };
  }

  const total = Math.max(1, normalizeNumber(settings?.numberOfWeeks, 1));
  const anchorDate = normalizeDateInput(settings?.lastCheckedDateForDayWeeklyCycleChange || getDebugOrTodayDate());
  const anchorLetter = String(settings?.currentWeek || "A").toUpperCase();
  const anchorIndex = mod(anchorLetter.charCodeAt(0) - 65, total);
  const offset =
    settings?.cycleType === "week"
      ? countIsoWeekOffset(anchorDate, date)
      : countWeekdayOffset(anchorDate, date);
  const scheduleIndex = mod(anchorIndex + offset, total);
  const scheduleKey = String.fromCharCode(65 + scheduleIndex);
  const template = createLegacyTemplate(settings, scheduleKey);

  return {
    templateId: template.id,
    template,
    scheduleKey,
    dayName: template.dayMode === "mondayOnly" ? "Monday" : actualDayName,
    source: "legacy",
    date,
    dateKey: formatDateKey(date),
    label: template.label,
  };
};

const isDateWithinRange = (dateKey, startDate, endDate) => {
  if (!startDate) return false;
  const normalizedEndDate = endDate || startDate;
  return startDate <= dateKey && dateKey <= normalizedEndDate;
};

const pickHighestPriorityMatch = (items = []) =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const priorityDiff = normalizeNumber(b.item?.priority, 0) - normalizeNumber(a.item?.priority, 0);
      if (priorityDiff !== 0) return priorityDiff;
      return b.index - a.index;
    })[0]?.item || null;

export const resolveScheduleForDate = (settings = {}, activeDate) => {
  const date = normalizeDateInput(activeDate);
  const dateKey = formatDateKey(date);
  const { assignments } = getScheduleCalendarConfig(settings);
  const templatesById = new Map(getAllScheduleTemplates(settings).map((template) => [template.id, template]));

  const exactAssignment = pickHighestPriorityMatch(
    assignments.filter((assignment) => assignment?.active !== false && (assignment?.date === dateKey || (assignment?.startDate === dateKey && !assignment?.endDate)))
  );

  const rangeAssignment = exactAssignment
    ? null
    : pickHighestPriorityMatch(
        assignments.filter(
          (assignment) =>
            assignment?.active !== false &&
            !assignment?.date &&
            isDateWithinRange(dateKey, assignment?.startDate, assignment?.endDate)
        )
      );

  const matchedRule = exactAssignment || rangeAssignment;
  const matchedTemplate = matchedRule?.templateId ? templatesById.get(matchedRule.templateId) : null;

  if (matchedRule?.templateId === NO_SCHOOL_TEMPLATE_ID) {
    return {
      date,
      dateKey,
      templateId: NO_SCHOOL_TEMPLATE_ID,
      template: null,
      scheduleKey: null,
      dayName: getDayNameForDate(date),
      label: "No School",
      source: "calendarAssignment",
      matchedRule,
      isAutomationPaused: Boolean(settings?.alteredSchedule || matchedRule?.pauseAutomations),
      isNoSchool: true,
    };
  }

  if (matchedRule?.templateId === NO_SCHEDULE_TEMPLATE_ID) {
    return {
      date,
      dateKey,
      templateId: NO_SCHEDULE_TEMPLATE_ID,
      template: null,
      scheduleKey: null,
      dayName: getDayNameForDate(date),
      label: "No Schedule",
      source: "calendarAssignment",
      matchedRule,
      isAutomationPaused: true,
      isNoSchool: false,
    };
  }

  if (matchedTemplate) {
    const resolvedDayName = matchedTemplate.dayMode === "mondayOnly" ? "Monday" : getDayNameForDate(date);
    return {
      date,
      dateKey,
      templateId: matchedTemplate.id,
      template: matchedTemplate,
      scheduleKey: matchedTemplate.scheduleKey,
      dayName: resolvedDayName,
      label: matchedTemplate.label,
      source: "calendarAssignment",
      matchedRule,
      isAutomationPaused: Boolean(settings?.alteredSchedule || matchedTemplate.pauseAutomations || matchedRule?.pauseAutomations),
      isNoSchool: false,
    };
  }

  return {
    date,
    dateKey,
    templateId: null,
    template: null,
    scheduleKey: null,
    dayName: getDayNameForDate(date),
    label: "No Schedule",
    source: "unassigned",
    matchedRule: null,
    isAutomationPaused: Boolean(settings?.alteredSchedule),
    isNoSchool: false,
  };
};

export const parseScheduleTimeOnDate = (activeDate, timeString) => {
  if (!timeString || typeof timeString !== "string") return null;

  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const parsedDate = normalizeDateInput(activeDate);
  parsedDate.setHours(hours, minutes, Number.isFinite(seconds) ? seconds : 0, 0);
  return parsedDate;
};

export const getCalendarOnlyClassBlocksForDate = ({
  classId,
  userSettings = {},
  activeDate,
}) => {
  const resolvedSchedule = resolveScheduleForDate(userSettings, activeDate);

  if (!resolvedSchedule?.templateId || resolvedSchedule.isNoSchool) {
    return { resolvedSchedule, blocks: [] };
  }

  const templateClasses = Array.isArray(resolvedSchedule.template?.classes)
    ? resolvedSchedule.template.classes.filter(
      (scheduleClass) => !classId || String(scheduleClass?.classId) === String(classId)
    )
    : [];

  if (templateClasses.length) {
    return {
      resolvedSchedule,
      blocks: templateClasses
        .filter((scheduleClass) => scheduleClass?.startTime && scheduleClass?.endTime)
        .map((scheduleClass) => ({
          startTime: String(scheduleClass.startTime),
          endTime: String(scheduleClass.endTime),
          classId: scheduleClass.classId ? String(scheduleClass.classId) : null,
        })),
    };
  }

  return { resolvedSchedule, blocks: [] };
};

/**
 * True when the class appears on the Schedule Manager template assigned to that date.
 * Counts meeting days (not periods): one appearance on the day is enough.
 */
export const doesClassMeetOnCalendarDate = ({
  classId,
  userSettings = {},
  activeDate,
} = {}) => {
  if (!classId) return false;

  const resolvedSchedule = resolveScheduleForDate(userSettings, activeDate);
  if (!resolvedSchedule?.templateId || resolvedSchedule.isNoSchool || !resolvedSchedule.template) {
    return false;
  }

  const templateClasses = Array.isArray(resolvedSchedule.template.classes)
    ? resolvedSchedule.template.classes
    : [];

  return templateClasses.some(
    (scheduleClass) => String(scheduleClass?.classId || "") === String(classId)
  );
};

/**
 * Count calendar days from startDate through targetDate (inclusive) where the class meets,
 * honoring Schedule Manager assignments and optional excluded date keys (YYYY-MM-DD).
 */
export const countCalendarClassMeetingDays = ({
  classId,
  userSettings = {},
  startDate,
  targetDate,
  excludedDays = [],
} = {}) => {
  if (!classId || targetDate == null || targetDate === "") return 0;

  const start = normalizeDateInput(startDate ?? new Date());
  const target = normalizeDateInput(targetDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(target.getTime()) || start > target) {
    return 0;
  }

  const excluded = new Set(
    (Array.isArray(excludedDays) ? excludedDays : [])
      .map((day) => String(day || "").trim())
      .filter(Boolean)
  );

  let count = 0;
  const current = new Date(start.getTime());
  while (current.getTime() <= target.getTime()) {
    const dateKey = formatDateKey(current);
    if (
      !excluded.has(dateKey)
      && doesClassMeetOnCalendarDate({
        classId,
        userSettings,
        activeDate: current,
      })
    ) {
      count += 1;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
};

const pickEarliestStartBlock = (candidates = []) => {
  const timedCandidates = candidates
    .map((candidate) => {
      const startTime = typeof candidate?.startTime === "string" ? candidate.startTime.trim() : "";
      if (!startTime) return null;
      const startMinutes = parseTimeToMinutes(startTime);
      if (startMinutes === null) return null;
      const endTime = typeof candidate?.endTime === "string" ? candidate.endTime.trim() : "";
      return { startTime, endTime, startMinutes };
    })
    .filter(Boolean)
    .sort((first, second) => first.startMinutes - second.startMinutes);

  return timedCandidates[0] || null;
};

const pickEarliestStartTime = (candidates = []) => (
  pickEarliestStartBlock(candidates)?.startTime || ""
);

const collectLegacyClassBlocks = (classItem = null) => {
  const schedule = isPlainObject(classItem?.schedule) ? classItem.schedule : null;
  if (!schedule) return [];

  return Object.values(schedule).flatMap((weekSchedule) => {
    if (!isPlainObject(weekSchedule)) return [];

    return Object.values(weekSchedule)
      .filter((entry) => isPlainObject(entry) && entry.active)
      .map((entry) => ({
        startTime: typeof entry.startTime === "string" ? entry.startTime : "",
        endTime: typeof entry.endTime === "string" ? entry.endTime : "",
      }))
      .filter((entry) => entry.startTime);
  });
};

/**
 * Resolve a class start time for agenda editor presets.
 * Prefer the active date's meeting time, but fall back to any scheduled occurrence
 * so teachers can still pick class-relative start options while planning ahead.
 */
export const resolveClassStartTimeForAgendaPresets = ({
  classId,
  userSettings = {},
  activeDate,
  classItem = null,
} = {}) => {
  const { blocks } = getCalendarOnlyClassBlocksForDate({
    classId,
    userSettings,
    activeDate,
  });
  const activeDateStartTime = pickEarliestStartTime(blocks);
  if (activeDateStartTime) {
    return activeDateStartTime;
  }

  const templateStartCandidates = getCustomScheduleTemplates(userSettings).flatMap((template) =>
    (Array.isArray(template?.classes) ? template.classes : [])
      .filter((scheduleClass) => !classId || String(scheduleClass?.classId) === String(classId))
      .map((scheduleClass) => ({
        startTime: typeof scheduleClass?.startTime === "string" ? scheduleClass.startTime : "",
        endTime: typeof scheduleClass?.endTime === "string" ? scheduleClass.endTime : "",
      }))
  );
  const templateStartTime = pickEarliestStartTime(templateStartCandidates);
  if (templateStartTime) {
    return templateStartTime;
  }

  return pickEarliestStartTime(collectLegacyClassBlocks(classItem));
};

/**
 * Resolve a class end time for agenda editor presets.
 * Uses the same scheduled block as resolveClassStartTimeForAgendaPresets.
 */
export const resolveClassEndTimeForAgendaPresets = ({
  classId,
  userSettings = {},
  activeDate,
  classItem = null,
} = {}) => {
  const { blocks } = getCalendarOnlyClassBlocksForDate({
    classId,
    userSettings,
    activeDate,
  });
  const activeDateEndTime = pickEarliestStartBlock(blocks)?.endTime || "";
  if (activeDateEndTime) {
    return activeDateEndTime;
  }

  const templateEndCandidates = getCustomScheduleTemplates(userSettings).flatMap((template) =>
    (Array.isArray(template?.classes) ? template.classes : [])
      .filter((scheduleClass) => !classId || String(scheduleClass?.classId) === String(classId))
      .map((scheduleClass) => ({
        startTime: typeof scheduleClass?.startTime === "string" ? scheduleClass.startTime : "",
        endTime: typeof scheduleClass?.endTime === "string" ? scheduleClass.endTime : "",
      }))
  );
  const templateEndTime = pickEarliestStartBlock(templateEndCandidates)?.endTime || "";
  if (templateEndTime) {
    return templateEndTime;
  }

  return pickEarliestStartBlock(collectLegacyClassBlocks(classItem))?.endTime || "";
};

export const SCHEDULE_BLOCK_CLASS_START_WINDOW_MS = 10 * 60 * 1000;

export const findOverlappingScheduleBlock = (
  blocks = [],
  nowDate,
  {
    includeBlockEnd = true,
    includeClassStartWindow = false,
    classStartWindowMs = SCHEDULE_BLOCK_CLASS_START_WINDOW_MS,
  } = {}
) => {
  const now = nowDate instanceof Date && !Number.isNaN(nowDate.getTime())
    ? new Date(nowDate.getTime())
    : new Date();
  const dayAnchor = normalizeDateInput(now);

  for (const block of blocks) {
    const startTime = parseScheduleTimeOnDate(dayAnchor, block.startTime);
    const endTime = parseScheduleTimeOnDate(dayAnchor, block.endTime);
    if (!startTime || !endTime || endTime <= startTime) continue;
    const isWithinBlock = now >= startTime && (includeBlockEnd ? now <= endTime : now < endTime);
    const isNearClassStart = includeClassStartWindow
      && now >= new Date(startTime.getTime() - classStartWindowMs)
      && now <= new Date(startTime.getTime() + classStartWindowMs);
    if (isWithinBlock || isNearClassStart) {
      return { ...block, startTime, endTime };
    }
  }

  return null;
};

export const findActiveScheduleBlock = (blocks = [], nowDate) =>
  findOverlappingScheduleBlock(blocks, nowDate, { includeBlockEnd: false });

export const createScheduleCalendarDefaults = () => ({
  templates: [],
  assignments: [],
});

export const LEGACY_TEMPLATE_ID_PREFIX = LEGACY_TEMPLATE_PREFIX;
